import { Controller } from "@hotwired/stimulus"
import { collectAddOnSelections } from "./add_on_serializer"

// Fetches the authoritative price breakdown (Pricing::Quote) from the server
// whenever the dates or guest count change, renders it into the summary card,
// and broadcasts the firm total so the Stripe Payment Element can sync its
// amount. Replaces the old inline legacy-math script — the displayed total now
// always matches what Stripe actually charges.
export default class extends Controller {
  static targets = ["checkInDate", "checkOutDate", "guestCount", "breakdown"]
  static values = { quoteUrl: String, propertyId: Number }

  connect() {
    if (this.#hasValidRange()) this.calculatePricing()
  }

  async calculatePricing() {
    if (!this.hasQuoteUrlValue) return
    // Dates cleared/incomplete: drop the stale breakdown rather than leaving a
    // price that no longer matches any selected range.
    if (!this.#hasValidRange()) { this.#resetBreakdown(); return }

    try {
      const res = await fetch(this.quoteUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content,
        },
        body: JSON.stringify({
          property_id: this.propertyIdValue,
          check_in_date: this.checkInDateTarget.value,
          check_out_date: this.checkOutDateTarget.value,
          guest_count: this.hasGuestCountTarget ? this.guestCountTarget.value : 1,
          add_ons: collectAddOnSelections(this.element),
          packages: this.#collectPackages(),
          trophy: this.#collectTrophy(),
        }),
      })
      const data = await res.json()
      if (data.html && this.hasBreakdownTarget) this.breakdownTarget.innerHTML = data.html
      if (typeof data.firm_total_cents === "number") {
        // Picked up by stripe_reservation_controller to update the Element amount.
        this.dispatch("quoted", { detail: { amountCents: data.firm_total_cents } })
      }
    } catch (_e) {
      // Network hiccup — leave the last good breakdown in place.
    }
  }

  // role => chosen PropertyLodgingPackage id, from the package-picker radios.
  #collectPackages() {
    const out = {}
    this.element.querySelectorAll('input[name^="packages["]:checked').forEach((el) => {
      const m = el.name.match(/packages\[(.+)\]/)
      if (m) out[m[1]] = el.value
    })
    return out
  }

  // { "<pat_id>": { quantity, tier } } from the trophy-picker inputs.
  #collectTrophy() {
    const out = {}
    this.element.querySelectorAll('input[name^="trophy["]').forEach((el) => {
      const m = el.name.match(/trophy\[(.+?)\]\[quantity\]/)
      if (!m) return
      if (!parseInt(el.value, 10)) return
      const tier = this.element.querySelector(`select[name="trophy[${m[1]}][tier]"]`)
      out[m[1]] = { quantity: el.value, tier: tier ? tier.value : undefined }
    })
    return out
  }

  #resetBreakdown() {
    if (this.hasBreakdownTarget) {
      this.breakdownTarget.innerHTML =
        '<p class="text-muted small mb-0"><i class="fas fa-calendar-alt me-1"></i> Select your dates to see a full price breakdown.</p>'
    }
    // Tell the Payment Element the amount is no longer firm (it ignores 0).
    this.dispatch("quoted", { detail: { amountCents: 0 } })
  }

  #hasValidRange() {
    if (!this.hasCheckInDateTarget || !this.hasCheckOutDateTarget) return false
    const ci = this.checkInDateTarget.value
    const co = this.checkOutDateTarget.value
    return !!ci && !!co && new Date(co) > new Date(ci)
  }
}
